/**
 * useAccountLanguageRestore — the account's language becomes the session
 * language, once per sign-in, on whatever route the user lands.
 *
 * `users/{uid}.lang` is the source of the language. A fresh device has no
 * stored language and starts in the browser's, so something has to apply the
 * account's value after sign-in. That "something" used to be the `useAuth`
 * bootstrap, but that hook runs only on the gym page, the gym selector and the
 * OAuth consent page. A direct load of `/profile`, `/admin`, `/calendar`,
 * `/wrapped/:year` or a legal page never ran it, so the profile's Appearance
 * selector showed the browser's language and writing the same value back did
 * nothing.
 *
 * This hook is called once from the app root (`App`), above the route table,
 * and reads the one `users/{uid}` listener of `CurrentUserProvider` — so it
 * covers every route by construction, with no per-route wiring to forget.
 *
 * Once per uid: a signed-in user changes the language only in Settings, which
 * writes the account, so later snapshots must not override a local change in
 * flight. Signing out re-arms it for the next sign-in.
 */
import { useEffect, useRef } from "react";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { useTranslation } from "../i18n";

export function useAccountLanguageRestore() {
  const { authUser, userData } = useCurrentUser();
  const { setLang } = useTranslation();
  const restoredUidRef = useRef(null);
  const uid = authUser?.uid || null;

  useEffect(() => {
    if (!uid) { restoredUidRef.current = null; return; }
    if (restoredUidRef.current === uid) return;
    // The provider keeps the previous user's data until the next snapshot
    // lands: only the signed-in user's own document counts.
    if (!userData || userData.uid !== uid) return;
    restoredUidRef.current = uid;
    // A legacy document without `lang` keeps the session language (the
    // sign-in bootstrap fills the field); `setLang` ignores an unsupported
    // value and persists the choice to localStorage.
    if (userData.lang) setLang(userData.lang);
  }, [uid, userData, setLang]);
}
